import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Badge, Button, Card, DataTable, EmptyState, Toast, type Column } from '../../design';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/clients';

interface OrderItem {
  name: string;
  productId: string | null;
  matched: boolean;
}

interface WooOrder {
  id: string;
  wooOrderId: string;
  email: string;
  client: { id: string; name: string } | null;
  items: OrderItem[];
  createdAt: string;
}

/** Store orders synced by the WooCommerce webhook — practitioner/admin only. */
export function OrdersScreen() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<WooOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<{ orders: WooOrder[] }>('/orders')
      .then((r) => setOrders(r.orders))
      .catch(() => setError('Could not load orders.'))
      .finally(() => setLoading(false));
  }, []);

  const columns: Column<WooOrder>[] = [
    { key: 'wooOrderId', header: 'Order', render: (o) => <span style={{ color: 'var(--text-strong)' }}>#{o.wooOrderId}</span> },
    {
      key: 'client',
      header: 'Client',
      render: (o) =>
        o.client ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            <Avatar name={o.client.name} size="sm" />
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ color: 'var(--text-strong)' }}>{o.client.name}</span>
              <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>{o.email}</span>
            </span>
          </span>
        ) : (
          <span style={{ color: 'var(--text-muted)' }}>{o.email}</span>
        ),
    },
    {
      key: 'items',
      header: 'Items',
      render: (o) => (
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', alignItems: 'center' }}>
          {o.items.length === 0 ? (
            <span style={{ color: 'var(--text-muted)' }}>—</span>
          ) : (
            o.items.map((item, i) => (
              <Badge key={i} tone={item.matched ? 'brand' : 'neutral'}>
                {item.name}
              </Badge>
            ))
          )}
        </span>
      ),
    },
    { key: 'date', header: 'Date', align: 'right', render: (o) => formatDate(o.createdAt) },
  ];

  return (
    <>
      {error && (
        <Toast tone="danger" title="Something went wrong" onDismiss={() => setError(null)}>
          {error}
        </Toast>
      )}
      <Card pad="none">
        {loading ? (
          <div style={{ padding: 'var(--space-6)', color: 'var(--text-muted)' }}>Loading…</div>
        ) : orders.length === 0 ? (
          <EmptyState
            icon="shopping-bag"
            title="No store orders yet"
            action={
              <Button size="sm" variant="secondary" icon="external-link" onClick={() => window.open('https://zenmindware.com', '_blank', 'noopener')}>
                Open the store
              </Button>
            }
          >
            Orders appear here as clients buy on zenmindware.com. Purchases of mapped programs unlock listening
            automatically.
          </EmptyState>
        ) : (
          <DataTable rows={orders} columns={columns} onRowClick={(o) => o.client && navigate(`/clients/${o.client.id}`)} />
        )}
      </Card>
    </>
  );
}
